import { ManualResolutionKind, ManualResolutionSaveResponse } from "./logRecords";

export interface ManualResolutionWording {
  title: string;
  /** Tooltip of the row action on an ERROR employee. */
  resolveHint: string;
  /** Tooltip of the row action on an employee resolved by hand. */
  reviewHint: string;
  /** The dialog's one row when there is no file to tick. */
  employeeRowTitle: string;
  employeeRowHint: string;
  undoWarning: string;
}

const WORDING: Record<ManualResolutionKind, ManualResolutionWording> = {
  upload: {
    title: "Manually resolved files",
    resolveHint: "Mark the files you uploaded by hand as resolved",
    reviewHint: "See or undo the files marked as resolved by hand",
    employeeRowTitle: "Employee marked as resolved (no files on record)",
    employeeRowHint:
      "Nothing to tick: the logs name no rejected file for this employee and the " +
      "Data Storage folder lists no files. Tick this row once the employee's " +
      "documents are in the portal.",
    undoWarning:
      "Un-ticking puts a file back to ERROR. If any rejected file remains, the " +
      "employee goes back to ERROR too.",
  },
  download: {
    title: "Manually resolved",
    resolveHint:
      "Mark the employee as resolved - you downloaded their documents by hand, or there are none",
    reviewHint: "See or undo the employee's manual resolution",
    employeeRowTitle: "Employee marked as resolved",
    employeeRowHint:
      "A download job is resolved for the employee as a whole. Tick this row once " +
      "the employee's documents are in Data Storage - downloaded by hand - or once " +
      "you know there is nothing to download. The employee then counts as a " +
      "success, and no error-mode run retries them.",
    undoWarning: "Un-ticking puts the employee back to ERROR, and an error-mode run retries them.",
  },
};

export function resolutionWording(kind: ManualResolutionKind): ManualResolutionWording {
  return WORDING[kind];
}

export function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

/** One sentence on what a save did, for the inline outcome and the toast alike. */
export function describeOutcome(
  outcome: ManualResolutionSaveResponse,
  label: string,
  kind: ManualResolutionKind = "upload",
): string {
  if (kind === "download") {
    return outcome.employee_resolved
      ? `${label} marked as resolved - counted as a success, no run retries them.`
      : `${label} un-resolved - back in ERROR.`;
  }
  const parts: string[] = [];
  if (outcome.resolved_files.length) {
    parts.push(`${plural(outcome.resolved_files.length, "file")} marked as resolved`);
  }
  if (outcome.undone_files.length) {
    parts.push(`${plural(outcome.undone_files.length, "file")} un-resolved`);
  }
  if (!parts.length && outcome.status_record === "append") parts.push(`${label} marked as resolved`);
  if (!parts.length && outcome.status_record === "remove") parts.push(`${label} un-resolved`);
  const head = parts.join(", ");
  if (outcome.employee_resolved) {
    return `${head}. ${label} is resolved - no rejected files remain.`;
  }
  const remaining = outcome.remaining_files.length;
  const reopened = outcome.undone_files.length > 0 || outcome.status_record === "remove";
  const verdict = reopened
    ? "is in ERROR again"
    : outcome.resolved_files.length
      ? "stays in ERROR"
      : "is in ERROR";
  const tail = remaining
    ? `${plural(remaining, "rejected file")} remain${remaining === 1 ? "s" : ""} - ${label} ${verdict}.`
    : `${label} ${verdict}.`;
  return `${head}; ${tail}`;
}
